/**
 * The producer's e-label as every provider reduces it. Pure and I/O-free: the phone parses, and
 * the edge parses again with the same code to verify the phone's claim, so no provider may behave
 * differently in the browser, in Node tests or in the Worker.
 */

/** A wine's colour as an e-label declares it; `unknown` is "not stated", never a guess. */
export const ELABEL_COLOURS = [
  "white",
  "red",
  "rose",
  "sparkling",
  "fortified",
  "unknown",
] as const;
export type ELabelColour = (typeof ELABEL_COLOURS)[number];

/** The e-label services proef can read; a label records which one it came from. */
export const ELABEL_PROVIDERS = [
  "scantrust",
  "vincod",
  "vitiquette",
  "qrambientale",
  "bottleinfo",
  "flabel",
  "evin",
] as const;
export type ELabelProviderId = (typeof ELABEL_PROVIDERS)[number];

/**
 * `empty`: the producer has not filled the label in (a valid, typed result, not an error);
 * `complete`: ingredients and energy are both stated; `partial`: something, but not both.
 */
export const ELABEL_STATUSES = ["complete", "partial", "empty"] as const;
export type ELabelStatus = (typeof ELABEL_STATUSES)[number];

/** Per-100 ml nutrition; `null` is "the producer did not state it". */
export interface ELabelNutrition {
  energyKj: number | null;
  energyKcal: number | null;
  fatG: number | null;
  saturatedFatG: number | null;
  carbohydrateG: number | null;
  sugarG: number | null;
  proteinG: number | null;
  saltG: number | null;
  fibreG: number | null;
}

/** One packaging part and how to dispose of it, in the producer's own words. */
export interface ELabelRecycling {
  component: string;
  material: string;
  instruction: string;
}

export interface ELabelFacts {
  provider: ELabelProviderId;
  status: ELabelStatus;
  /** The provider's public record id (never a secret): with `provider` it names one label. */
  sku: string;
  /** Lowercase language the text was read in; `null` for a provider that has no language choice. */
  lang: string | null;
  productName: string;
  producerName: string;
  /** ISO 3166-1 alpha-2. */
  originCountries: string[];
  colour: ELabelColour;
  grapes: string[];
  vintage: number | null;
  abvPercent: number | null;
  /** Bottle volume in ml. */
  volumeMl: number | null;
  ingredients: {
    items: string[];
    /** The producer's own wording per language, HTML stripped. */
    freeText: Record<string, string>;
  };
  nutritionPer100ml: ELabelNutrition;
  allergens: string[];
  recycling: ELabelRecycling[];
  servingMl: number | null;
  sourceUpdatedAt: string | null;
}

/** The input is not an e-label of this provider at all (as opposed to a sparse or empty one). */
export class ELabelParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ELabelParseError";
  }
}

export function emptyNutrition(): ELabelNutrition {
  return {
    energyKj: null,
    energyKcal: null,
    fatG: null,
    saturatedFatG: null,
    carbohydrateG: null,
    sugarG: null,
    proteinG: null,
    saltG: null,
    fibreG: null,
  };
}

/** What a parser states; everything else defaults to "not stated". */
export interface ELabelDraft extends Partial<
  Omit<ELabelFacts, "provider" | "status" | "sku" | "ingredients" | "nutritionPer100ml">
> {
  ingredients?: Partial<ELabelFacts["ingredients"]>;
  nutritionPer100ml?: Partial<ELabelNutrition>;
}

function hasNutrition(n: ELabelNutrition): boolean {
  return Object.values(n).some((value) => value !== null);
}

/** `empty` when nothing is stated, `complete` when ingredients and energy both are, else `partial`. */
export function deriveELabelStatus(label: Omit<ELabelFacts, "status">): ELabelStatus {
  const hasIngredients =
    label.ingredients.items.length > 0 || Object.keys(label.ingredients.freeText).length > 0;
  const hasEnergy =
    label.nutritionPer100ml.energyKj !== null || label.nutritionPer100ml.energyKcal !== null;
  if (hasIngredients && hasEnergy) return "complete";
  const stated =
    hasIngredients ||
    hasNutrition(label.nutritionPer100ml) ||
    label.productName !== "" ||
    label.producerName !== "" ||
    label.originCountries.length > 0 ||
    label.colour !== "unknown" ||
    label.grapes.length > 0 ||
    label.vintage !== null ||
    label.abvPercent !== null ||
    label.volumeMl !== null ||
    label.allergens.length > 0 ||
    label.recycling.length > 0 ||
    label.servingMl !== null;
  return stated ? "partial" : "empty";
}

/**
 * The one way a provider builds its label: defaults fill what the page did not state and the
 * status is derived, so no parser can forget a field or invent a status.
 */
export function buildELabel(
  provider: ELabelProviderId,
  sku: string,
  draft: ELabelDraft = {},
): ELabelFacts {
  const label: Omit<ELabelFacts, "status"> = {
    provider,
    sku,
    lang: draft.lang ?? null,
    productName: draft.productName ?? "",
    producerName: draft.producerName ?? "",
    originCountries: draft.originCountries ?? [],
    colour: draft.colour ?? "unknown",
    grapes: draft.grapes ?? [],
    vintage: draft.vintage ?? null,
    abvPercent: draft.abvPercent ?? null,
    volumeMl: draft.volumeMl ?? null,
    ingredients: {
      items: draft.ingredients?.items ?? [],
      freeText: draft.ingredients?.freeText ?? {},
    },
    nutritionPer100ml: { ...emptyNutrition(), ...draft.nutritionPer100ml },
    allergens: draft.allergens ?? [],
    recycling: draft.recycling ?? [],
    servingMl: draft.servingMl ?? null,
    sourceUpdatedAt: draft.sourceUpdatedAt ?? null,
  };
  return { ...label, status: deriveELabelStatus(label) };
}

/** A label the producer has not filled in: a valid result that says "nothing here yet". */
export function emptyELabel(
  provider: ELabelProviderId,
  sku: string,
  lang: string | null = null,
): ELabelFacts {
  return buildELabel(provider, sku, { lang });
}
